/**
 * Zod schemas for the Play Manager (playmanagerv0 Phase 2).
 *
 * Canonical shapes for the 3-tier play model's middle tier (FlowPlayType) and
 * its derived plays. A derived play (play type × source instance) has no DB
 * row — its identity is the composite (playTypeId, instanceRef) — so
 * DerivedPlaySchema is the wire/service shape, not a table mirror.
 *
 * No override layers (Spencer, 2026-07-21): the connector's
 * handlerConfig.callbackUrl is THE destination (editable in the Plays admin),
 * and the SFDC campaign is the source of truth for copy — marketing edits the
 * base campaign; reps edit per-send in the enroll panel. A FlowPlayOverride
 * row exists only to pause a single play.
 *
 * Shared (importable by web via @shared): the admin UI and admin API validate
 * against the same schemas. Types via z.infer only.
 */

import { z } from 'zod';

// Enums

/**
 * Exposure kinds with a registered deriver in
 * src/services/flow/play-derivation.ts. The DB column is a plain String —
 * adding a kind is a code change (schema value + deriver), no migration.
 *
 * - `sfdc_visible_flag` — event_invite: many plays derived from visible SFDC
 *   campaigns (Phase 2).
 * - `singleton` — a source-less/contact-only play type (generate_message):
 *   the play type exposes exactly ONE derived play (instanceRef = slug),
 *   live whenever the play type is active. The queue is filled from contacts,
 *   not from a source instance.
 * - `gong_flow` — Gong Flow Play + Queue redesign (U6): one derived play per
 *   row in the `gong_flows` mirror table. No admin curation — all synced
 *   flows are live; ranking + fail-loud replaces activation ceremony (see
 *   docs/flow/gong-flow-play-spec-handoff.md §1). The deriver itself lands
 *   in U8 (`play-derivation.ts`); this member exists now so downstream units
 *   (schemas, admin UI) can reference it ahead of that landing.
 * - `none` — a play type with NO exposed plays at all, deliberately, unlike
 *   `singleton`'s exactly-one. Launcher V2 PR C (U8) registers its three
 *   sequence play types (`gong_engage`/`inflection`/`slack_copilot`) this way:
 *   registration-only, with the real per-play exposure model left for PR D
 *   to design once the durable-send leg is wired. Distinct from a play type
 *   simply being `draft` (which already hides it from the queue) — `none`
 *   means "this exposure model isn't designed yet," not "temporarily off."
 */
export const FlowExposureKindSchema = z.enum(['sfdc_visible_flag', 'singleton', 'gong_flow', 'none']);
export type FlowExposureKind = z.infer<typeof FlowExposureKindSchema>;

export const FlowPlayTypeStatusSchema = z.enum(['draft', 'active', 'paused', 'archived']);
export type FlowPlayTypeStatusValue = z.infer<typeof FlowPlayTypeStatusSchema>;

/** Status of one derived play. Default is live; an override row stores paused. */
export const FlowPlayStatusSchema = z.enum(['live', 'paused']);
export type FlowPlayStatusValue = z.infer<typeof FlowPlayStatusSchema>;

export const FlowTestStatusSchema = z.enum(['pass', 'fail']);
export type FlowTestStatusValue = z.infer<typeof FlowTestStatusSchema>;

// Destination test result (handler-dispatched, clay-routine-destination-
// test-plan Unit 2). One shape per connector handler kind — webhook's test
// necessarily makes a real HTTP call (that IS the test); clay_routine's is a
// side-effect-free preflight (see src/services/execution-handlers/clay-
// routine.ts's testClayRoutineDestination header). The admin route
// (admin-flow-play-types.ts POST /:slug/test) returns this shape verbatim;
// the frontend discriminates rendering on `kind`.

export const WebhookDestinationTestResultSchema = z.object({
  kind: z.literal('webhook'),
  lastTestStatus: FlowTestStatusSchema,
  latencyMs: z.number(),
  /** The exact URL the test POSTed to, or null when none is configured yet. */
  url: z.string().nullable(),
  /** HTTP status received, or null when no request was attempted (no URL). */
  httpStatus: z.number().nullable(),
  body: z.string(),
  /** referenceKey of the test row — find it in the receiving Clay table. */
  referenceKey: z.string().optional(),
});
export type WebhookDestinationTestResult = z.infer<typeof WebhookDestinationTestResultSchema>;

/** One check in a clay_routine preflight — see testClayRoutineDestination's header for the 4 checks. */
export const ClayRoutineDestinationCheckSchema = z.object({
  name: z.string(),
  ok: z.boolean(),
  detail: z.string(),
});
export type ClayRoutineDestinationCheck = z.infer<typeof ClayRoutineDestinationCheckSchema>;

export const ClayRoutineDestinationTestResultSchema = z.object({
  kind: z.literal('clay_routine'),
  lastTestStatus: FlowTestStatusSchema,
  latencyMs: z.number(),
  routineId: z.string().nullable(),
  checks: z.array(ClayRoutineDestinationCheckSchema),
});
export type ClayRoutineDestinationTestResult = z.infer<typeof ClayRoutineDestinationTestResultSchema>;

/**
 * One check in the gong_flow preflight (`testGongFlowDestination`,
 * `execution-handlers/gong-flow.ts`) — a credential/config-only preflight
 * (U10): Gong credentials come from `process.env` inside the handler, never
 * `handlerConfig`, so this never makes a live Gong API call (unlike
 * webhook's real POST or clay_routine's `GET /me`) and NEVER calls
 * `assignProspects` (no real assign — see the handler's module doc comment).
 */
export const GongFlowDestinationCheckSchema = z.object({
  name: z.string(),
  ok: z.boolean(),
  detail: z.string(),
});
export type GongFlowDestinationCheck = z.infer<typeof GongFlowDestinationCheckSchema>;

export const GongFlowDestinationTestResultSchema = z.object({
  kind: z.literal('gong_flow'),
  lastTestStatus: FlowTestStatusSchema,
  latencyMs: z.number(),
  checks: z.array(GongFlowDestinationCheckSchema),
});
export type GongFlowDestinationTestResult = z.infer<typeof GongFlowDestinationTestResultSchema>;

/** Discriminated on `kind` — the frontend's test-result renderer branches on it. */
export const PlayDestinationTestResultSchema = z.discriminatedUnion('kind', [
  WebhookDestinationTestResultSchema,
  ClayRoutineDestinationTestResultSchema,
  GongFlowDestinationTestResultSchema,
]);
export type PlayDestinationTestResult = z.infer<typeof PlayDestinationTestResultSchema>;

// JSON column shapes (read via parseDbJson at call sites)

/**
 * exposure_config column. sfdc_visible_flag needs no config today; the shape
 * is an open record so future kinds can carry parameters without a migration.
 */
export const ExposureConfigSchema = z.record(z.string(), z.unknown());
export type ExposureConfig = z.infer<typeof ExposureConfigSchema>;

// Derived play (service/wire shape — no table behind it)

export const DerivedPlaySchema = z.object({
  playTypeId: z.number().int(),
  playTypeSlug: z.string(),
  /** Source-instance identity — SFDC campaign id for sfdc_visible_flag. */
  instanceRef: z.string(),
  name: z.string().nullable(),
  status: FlowPlayStatusSchema,
  /** ISO date (yyyy-mm-dd) or null. */
  startDate: z.string().nullable(),
  inviteSubject: z.string().nullable(),
  inviteBody: z.string().nullable(),
  enrollmentCount: z.number().int(),
});
export type DerivedPlay = z.infer<typeof DerivedPlaySchema>;

// Admin API response + request schemas

export const FlowPlayTypeResponseSchema = z.object({
  id: z.number().int(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  actionDefinitionId: z.number().int(),
  exposureKind: z.string(),
  exposureConfig: ExposureConfigSchema,
  status: FlowPlayTypeStatusSchema,
  lastTestedAt: z.string().nullable(),
  lastTestStatus: FlowTestStatusSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * The connector this play type wraps. `callbackUrl` is THE destination
   * for a `webhook` connector — code-configured `clay_routine` connectors
   * have no URL to paste; `routineId` is theirs instead (mutually exclusive
   * with callbackUrl by handler, never both meaningfully non-null).
   */
  connector: z.object({
    slug: z.string(),
    name: z.string(),
    handler: z.string(),
    callbackUrl: z.string().nullable(),
    routineId: z.string().nullable(),
  }),
});
export type FlowPlayTypeResponse = z.infer<typeof FlowPlayTypeResponseSchema>;

export const FlowPlayTypeListResponseSchema = z.array(FlowPlayTypeResponseSchema);

/**
 * PUT /api/v4/admin/flow-play-types/:slug response: the play type plus the
 * transient `requiresRetest`/`wasDemoted` flags, present only when the
 * callback URL moved. Not persisted.
 */
export const FlowPlayTypeUpdateResponseSchema = FlowPlayTypeResponseSchema.extend({
  requiresRetest: z.boolean().optional(),
  wasDemoted: z.boolean().optional(),
});

export const DerivedPlayListResponseSchema = z.array(DerivedPlaySchema);

/** PUT/DELETE /api/v4/admin/flow-play-types/:slug/plays/:instanceRef ack. `status` is absent on DELETE. */
export const FlowPlayOverrideAckResponseSchema = z.object({
  ok: z.literal(true),
  status: FlowPlayStatusSchema.nullable().optional(),
});

/**
 * PUT /api/v4/admin/flow-play-types/:slug — partial update. Strict so UI/server
 * drift surfaces as 400. slug/connector are immutable (code/seed-registered);
 * status transitions to `active` are additionally gated on lastTestStatus.
 * `callbackUrl` writes through to the connector ActionDefinition's
 * handlerConfig (merged — envelopeShape etc. preserved) so growth can
 * paste/re-paste the destination without a second config surface.
 */
export const UpdateFlowPlayTypeSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    exposureKind: FlowExposureKindSchema.optional(),
    exposureConfig: ExposureConfigSchema.optional(),
    callbackUrl: z.string().url().optional(),
    status: FlowPlayTypeStatusSchema.optional(),
  })
  .strict();
export type UpdateFlowPlayType = z.infer<typeof UpdateFlowPlayTypeSchema>;

/**
 * PUT /api/v4/admin/flow-play-types/:slug/plays/:instanceRef — pause one
 * derived play. DELETE clears the override (play returns to derived live).
 */
export const UpsertFlowPlayOverrideSchema = z
  .object({
    status: FlowPlayStatusSchema,
  })
  .strict();
export type UpsertFlowPlayOverride = z.infer<typeof UpsertFlowPlayOverrideSchema>;
